import React, { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";

export default function BarcodeScanner({ onScan, onClose }) {
    const [error, setError] = useState(null);
    const [lastScan, setLastScan] = useState(null);
    const [value, setValue] = useState("");
    const inputRef = useRef(null);

    const scanTimeoutMs = 500;
    const clearTimerRef = useRef(null);
    const allowedKeyRegex = useMemo(() => /^[a-zA-Z0-9\-._]$/, []);

    useEffect(() => {
        inputRef.current?.focus?.();
        return () => {
            if (clearTimerRef.current) {
                clearTimeout(clearTimerRef.current);
            }
        };
    }, []);

    function scheduleClearBuffer() {
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
        clearTimerRef.current = setTimeout(() => setValue(""), scanTimeoutMs);
    }

    function commitScan(raw) {
        const decodedText = (raw ?? "").trim();
        if (!decodedText) return;
        setLastScan(decodedText);
        setError(null);
        setValue("");
        onScan(decodedText);
    }

    function handleKeyDown(e) {
        // Don't interfere with manual input fields.
        const tag = e.target?.tagName?.toLowerCase?.();
        if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;

        if (e.key === "Enter") {
            commitScan(value);
            e.preventDefault();
            return;
        }
        if (e.key === "Escape") {
            setValue("");
            return;
        }
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (!allowedKeyRegex.test(e.key)) return;

        setValue((prev) => prev + e.key);
        scheduleClearBuffer();
    }

    return (
        <div className="scanner-overlay">
            <div className="scanner-panel" onKeyDown={handleKeyDown} tabIndex={-1}>
                {/* Header */}
                <div className="scanner-header">
                    <div>
                        <h3 className="scanner-title">USB Barcode Scanner</h3>
                        <p className="scanner-sub">Scan with your USB scanner (ends with Enter)</p>
                    </div>
                    <button className="report-close" onClick={onClose}>✕</button>
                </div>

                {/* USB scanner input */}
                <div className="scanner-viewport" style={{ padding: "12px" }}>
                    <div style={{ display: "grid", gap: "10px" }}>
                        <input
                            ref={inputRef}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    commitScan(value);
                                    e.preventDefault();
                                }
                            }}
                            placeholder="Click here, then scan (or type) and press Enter…"
                            autoFocus
                            style={{
                                width: "100%",
                                height: "42px",
                                padding: "0 12px",
                                background: "var(--elevated)",
                                border: "1px solid var(--border)",
                                borderRadius: "var(--radius)",
                                color: "var(--text)",
                                fontFamily: "var(--font)",
                                outline: "none",
                                fontSize: "13px",
                            }}
                        />
                        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                            <button
                                className="save-btn"
                                type="button"
                                onClick={() => commitScan(value)}
                                style={{ height: "36px", padding: "0 14px" }}
                            >
                                Submit
                            </button>
                            <button
                                className="secondary-btn"
                                type="button"
                                onClick={() => setValue("")}
                                style={{ height: "36px", padding: "0 14px" }}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                    {error && (
                        <div className="scanner-error" style={{ marginTop: "10px" }}>
                            <p>{error}</p>
                        </div>
                    )}
                </div>

                {/* Last scan result */}
                <div className="scanner-result">
                    {lastScan ? (
                        <div className="scanner-success">
                            <span>✅</span>
                            <p>Scanned: <strong>{lastScan}</strong></p>
                        </div>
                    ) : (
                        <p className="scanner-hint">
                            Click the input, then scan. Most USB scanners send an Enter at the end.
                        </p>
                    )}
                </div>

                {/* Manual entry fallback */}
                <div className="scanner-manual">
                    <p style={{ fontSize: "11px", color: "var(--text3)", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.6px" }}>
                        Or type barcode manually
                    </p>
                    <ManualEntry
                        onScan={(text) => {
                            setError(null);
                            setLastScan(text);
                            onScan(text);
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

function ManualEntry({ onScan }) {
    const [value, setValue] = useState("");

    function handleSubmit(e) {
        e.preventDefault();
        if (value.trim()) {
            onScan(value.trim());
            setValue("");
        }
    }

    return (
        <form onSubmit={handleSubmit} style={{ display: "flex", gap: "8px" }}>
            <input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Enter barcode or SKU..."
                autoFocus
                style={{
                    flex: 1, height: "38px", padding: "0 12px",
                    background: "var(--elevated)", border: "1px solid var(--border)",
                    borderRadius: "var(--radius)", color: "var(--text)",
                    fontFamily: "var(--font)", outline: "none", fontSize: "13px",
                }}
            />
            <button
                type="submit"
                className="save-btn"
                style={{ height: "38px", padding: "0 16px" }}
            >
                Add →
            </button>
        </form>
    );
}